import downloadFile from 'utils/downloadFile';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'lib/httpClient';
import { Api, FeeChargeType } from 'further-types/fees';
import { ApiResponse } from 'further-types/api';
import { useState } from 'react';

type CashMovement = {
  amount: number;
  amountExVat: number;
  vat: number;
  date: string;
  destination: 'INVESTOR_FEE';
  firmId: string;
  investorId: string;
  notes: string;
  source?: string;
};

export type InvestmentWithFee = {
  investmentId: string;
  investmentDate: string;
  investmentAmount: number;
  externalInvestmentId: string;
  investorId: {
    fullName: string;
  };
  newFee: Api.Fee;
};

export type InvestorFee = {
  investorId: {
    fullName: string;
  };
  cashMovement: CashMovement;
};

type CalculateInvestmentFeesResponse = {
  data: {
    investmentsWithFees: Array<InvestmentWithFee>;
    investmentsWithErrorsIds: Array<string>;
    investmentsWithDateErrors: Array<string>;
  };
  statusCode: number;
};

type CalculateInvestorFeesResponse = {
  data: {
    investorFees: Array<InvestorFee>;
    investmentsWithErrorsIds: Array<string>;
  };
  statusCode: number;
};

type FeeInput = {
  feeType: string;
  feeName: string;
  investmentIds: Array<string>;
  fundIds: Array<string>;
  amount: string;
  vat: string;
  date: Date;
};

type CreateInvestorFeesInput = {
  firmId: string;
  investorFees: Array<{
    cashMovement: CashMovement;
  }>;
};

const useCalculateInvestmentFees = () => {
  return useMutation<CalculateInvestmentFeesResponse, unknown, FeeInput>({
    mutationFn: async (data) => {
      const { data: result } = await api.post<CalculateInvestmentFeesResponse>(
        'fee/calculate-investment-fees',
        data,
      );
      return result;
    },
  });
};

const useCalculateInvestorFees = () => {
  return useMutation<CalculateInvestorFeesResponse, unknown, FeeInput>({
    mutationFn: async (data) => {
      const { data: result } = await api.post<CalculateInvestorFeesResponse>(
        `fee/calculate-investor-fees`,
        data,
      );
      return result;
    },
  });
};

const useCreateInvestorFees = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<unknown>, unknown, CreateInvestorFeesInput>({
    mutationFn: async (data) => {
      const { data: result } = await api.post<ApiResponse<unknown>>(
        `fee/create-investor-fees`,
        data,
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['one-off-fees-and-discounts'],
      });
    },
  });
};

const useCreateInvestmentFees = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<unknown>,
    unknown,
    Api.CreateInvestmentFeesRequest
  >({
    mutationFn: async (data) => {
      const { data: result } = await api.post<ApiResponse<unknown>>(
        'fee/create-investment-fees',
        data,
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['one-off-fees-and-discounts'],
      });
    },
  });
};

const useCreateFeesUploadFile = () => {
  const [isCreatingFeesUploadFile, setIsCreatingFeesUploadFile] =
    useState(false);

  return {
    createFeesUploadFile: async (
      fundIds: Array<string>,
      feeName: string,
      chargeTo: FeeChargeType,
      firmId: string,
    ) => {
      try {
        setIsCreatingFeesUploadFile(true);
        const response = await api.post(
          'fee/create-fees-upload-file',
          { fundIds, feeName, chargeTo, firmId },
          { responseType: 'blob' },
        );
        const contentType = response.headers['content-type'];
        if (
          typeof contentType === 'string' &&
          contentType.startsWith('application/vnd.openxmlformats')
        ) {
          downloadFile(
            response.data,
            `Fees-and-Discounts-Upload-Sheet-${Date.now()}.xlsx`,
          );
          setIsCreatingFeesUploadFile(false);
          return true;
        }
      } catch (_) {
        return false;
      }
      setIsCreatingFeesUploadFile(false);
      return false;
    },
    isCreatingFeesUploadFile,
  };
};

const useUploadFeesFile = () => {
  const [isUploadingFeesFile, setIsUploadingFeesFile] = useState(false);

  return {
    isUploadingFeesFile,
    uploadFeesFile: async (formData: FormData) => {
      setIsUploadingFeesFile(true);
      try {
        const result = await api.post('fee/upload-fees-file', formData);
        return result;
      } finally {
        setIsUploadingFeesFile(false);
      }
    },
  };
};

const useAddFeesAndDiscounts = () => {
  const { isPending: isCalculatingFees, mutateAsync: calculateInvestmentFees } =
    useCalculateInvestmentFees();
  const { isPending: isCreatingFee, mutateAsync: createInvestmentFee } =
    useCreateInvestmentFees();
  const { mutateAsync: calculateInvestorFees } = useCalculateInvestorFees();
  const { isPending: isCreatingInvestorFees, mutateAsync: createInvestorFees } =
    useCreateInvestorFees();
  const { createFeesUploadFile, isCreatingFeesUploadFile } =
    useCreateFeesUploadFile();
  const { uploadFeesFile, isUploadingFeesFile } = useUploadFeesFile();

  return {
    createFeesUploadFile,
    isCreatingFeesUploadFile,
    uploadFeesFile,
    isUploadingFeesFile,
    calculateInvestmentFees,
    calculateInvestorFees,
    createInvestmentFee,
    createInvestorFees,
    isCalculatingFees,
    isCreatingFee,
    isCreatingInvestorFees,
  };
};

export default useAddFeesAndDiscounts;
